import { useSetToast } from "@app/components/Toast";
import { StatusTagCircular } from "@design-system/DataDisplay/StatusTagCircular";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Chip } from "@design-system/Inputs/Chip";
import { Switch } from "@design-system/Inputs/Switch";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import {
    CheckboxCell,
    CheckboxHeader,
    type ColumnDef,
} from "@design-system/Table";
import { Text } from "@design-system/Typography/Text";
import {
    KbDocumentType,
    KnowledgeBaseItemStatus,
    KnowledgeBaseItemType,
    ResponseStatus,
    VisibilityStatus,
} from "@generated/client/graphql";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useDeleteCustomKnowledgeBaseItem,
    useUpdateCustomKnowledgeBaseItem,
    useUpdateKnowledgeBaseItem,
} from "../../data";
import { useKnowledgeBasePageTranslations } from "../../i18n";
import { getLink, type KnowledgeBaseListItem } from "../../service";
import { AddCustomItemModal } from "../AddCustomItemModal";
import { DeleteCustomItemModal } from "../DeleteModal";
import { useKnowledgeBaseTableTranslations } from "./i18n";
dayjs.extend(LocalizedFormat);

const selectCol: (canSelectAll: boolean) => ColumnDef<KnowledgeBaseListItem> = (
    canSelectAll,
) => ({
    id: "checkbox",
    enableSorting: false,
    header: ({ table }) => (
        <CheckboxHeader table={table} disabled={!canSelectAll} />
    ),
    cell: ({ row }) => (
        <CheckboxCell
            row={row}
            disabled={!row.original.businessLogicAllowsPoweringAI}
        />
    ),
    size: 50,
    meta: {
        colWidth: "50px",
        headerClassName: "min-w-[50px] max-w-[50px]",
        cellClassName:
            "min-w-[50px] max-w-[50px] group-hover/tablerow:bg-primary",
    },
});

const aiVisibilityStatusCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "aiVisibilityStatus",
    meta: {
        colWidth: "115px",
        headerClassName: "min-w-[115px] max-w-[115px] text-secondary",
        cellClassName:
            "min-w-[115px] max-w-[115px] group-hover/tablerow:bg-primary",
    },
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.aiVisibilityStatus);
    },
    cell: ({ row }) => {
        const { updateKnowledgeBaseItem } = useUpdateKnowledgeBaseItem(
            row.original.id,
        );
        const { setToastSuccess, setToastError } = useSetToast();
        const { t, keys } = useKnowledgeBaseTableTranslations();

        const status = row.original.aiVisibilityStatus;
        const businessLogicAllowsPoweringAi =
            row.original.businessLogicAllowsPoweringAI;

        const isActive =
            status === VisibilityStatus.Public &&
            businessLogicAllowsPoweringAi === true;

        const isDisabled = !businessLogicAllowsPoweringAi;

        const handleChange = (checked: boolean) => {
            const newStatus = checked
                ? VisibilityStatus.Public
                : VisibilityStatus.Private;

            updateKnowledgeBaseItem(
                {
                    aiVisibilityStatus: newStatus,
                },
                {
                    onSettled(data, error) {
                        if (
                            error ||
                            data?.updateKnowledgeBaseItem.status !==
                                ResponseStatus.Success
                        ) {
                            return setToastError(
                                t(keys.update_knowledge_base_error),
                            );
                        }
                        return setToastSuccess(
                            t(keys.updateKnowledge_base_success),
                        );
                    },
                },
            );
        };

        return (
            <Tooltip>
                <Tooltip.Trigger asChild>
                    <FlexRow className="justify-center">
                        <Switch
                            checked={isActive}
                            onChange={handleChange}
                            type="accent"
                            disabled={isDisabled}
                        />
                    </FlexRow>
                </Tooltip.Trigger>
                {isDisabled && (
                    <Tooltip.Content>
                        {t(keys.visibility_status_disabled_tooltip, {
                            context: row.original.type,
                        })}
                    </Tooltip.Content>
                )}
            </Tooltip>
        );
    },
};

const statusCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "status",
    accessorKey: "status",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.status);
    },
    cell: ({ row }) => {
        const status = row.original.status;
        const tagStatus =
            status === KnowledgeBaseItemStatus.Done
                ? "validated"
                : status === KnowledgeBaseItemStatus.InProgress
                  ? "inProgress"
                  : "todo";
        return <StatusTagCircular status={tagStatus} size="md" />;
    },
    meta: {
        colWidth: "71px",
        headerClassName: "min-w-[71px] max-w-[71px] text-secondary",
        cellClassName:
            "min-w-[71px] max-w-[71px] group-hover/tablerow:bg-primary",
    },
};

const nameCol = (activeGroupId: string): ColumnDef<KnowledgeBaseListItem> => ({
    id: "name",
    accessorKey: "name",
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.activeGroup, { context: activeGroupId });
    },
    cell: ({ row }) => {
        const {
            aiVisibilityStatus,
            businessLogicAllowsPoweringAI,
            status,
            type,
        } = row.original;

        const isItemActive =
            aiVisibilityStatus === VisibilityStatus.Public &&
            businessLogicAllowsPoweringAI === true;

        const isDocumentWithoutLastVersionApproved =
            type === KnowledgeBaseItemType.Document &&
            status !== KnowledgeBaseItemStatus.Done;

        const displayDocumentWarning =
            isItemActive && isDocumentWithoutLastVersionApproved;

        const isQuestionnaireInProgress =
            type === KnowledgeBaseItemType.Questionnaire &&
            status === KnowledgeBaseItemStatus.InProgress;

        const displayQuesstionnaireWarning =
            isItemActive && isQuestionnaireInProgress;

        const { t, keys } = useKnowledgeBaseTableTranslations();

        if (!row.original.name) {
            return "-";
        }
        if (displayDocumentWarning || displayQuesstionnaireWarning) {
            return (
                <FlexCol gap="1.5">
                    <Text>{row.original.name}</Text>
                    <Text pl="2.5" color="tertiary">
                        {t(keys.warning, {
                            context: type,
                        })}
                    </Text>
                </FlexCol>
            );
        }
        return row.original.name;
    },
    meta: {
        colWidth: "w-full min-w-[422px]",
        headerClassName: "w-full min-w-[422px] text-secondary",
        cellClassName: "w-full min-w-[422px] group-hover/tablerow:bg-primary",
    },
});

const projectNameCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "projectName",
    accessorKey: "projectName",
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.projectName);
    },
    cell: ({ row }) => row.original.framework?.projectName ?? "-",
    meta: {
        colWidth: "w-full min-w-[200px]",
        headerClassName: "w-full min-w-[200px] text-secondary",
        cellClassName: "w-full min-w-[200px] group-hover/tablerow:bg-primary",
    },
};

const lastModifiedAtCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "lastModifiedAt",
    accessorKey: "lastModifiedAt",
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.last_modified_at);
    },
    cell: ({ row }) =>
        row.original.lastModifiedAt
            ? dayjs(row.original.lastModifiedAt).format("LL") +
              " " +
              dayjs(row.original.lastModifiedAt).format("HH:mm")
            : "-",
    meta: {
        colWidth: "182px",
        headerClassName: "min-w-[182px] max-w-[182px] text-secondary",
        cellClassName:
            "min-w-[182px] max-w-[182px] group-hover/tablerow:bg-primary",
    },
};

const linkCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "link",
    accessorKey: "link",
    enableSorting: false,
    header: () => {
        return "";
    },
    cell: ({ row }) => {
        const navigate = useNavigate();
        const { org_uname } = useParams();
        const handleClick = () => {
            navigate(getLink(org_uname, row.original));
        };
        return (
            <FlexRow className="justify-center">
                <Button variant="outlined" size="sm" onClick={handleClick}>
                    <Icon name="external" size="sm" />
                </Button>
            </FlexRow>
        );
    },
    meta: {
        colWidth: "64px",
        headerClassName: "min-w-[64px] max-w-[64px] text-secondary",
        cellClassName:
            "min-w-[64px] max-w-[64px] group-hover/tablerow:bg-primary",
    },
};

const typeCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "type",
    accessorKey: "type",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.type);
    },
    cell: ({ row }) => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        const iconNameToShow =
            row.original?.document?.documentType === KbDocumentType.Policy
                ? "scale"
                : row.original?.document?.documentType ===
                    KbDocumentType.Measure
                  ? "target"
                  : "document";
        const textToShow =
            row.original?.document?.documentType === KbDocumentType.Policy
                ? t(keys.policy)
                : row.original?.document?.documentType ===
                    KbDocumentType.Measure
                  ? t(keys.measure)
                  : t(keys.customDocument);
        return (
            <Chip size="sm" interactive={false}>
                <Chip.Icon name={iconNameToShow} className="text-primary" />
                <Chip.Value>{textToShow}</Chip.Value>
            </Chip>
        );
    },
    meta: {
        colWidth: "179px",
        headerClassName: "min-w-[179px] max-w-[179px] text-secondary",
        cellClassName:
            "min-w-[179px] max-w-[179px] group-hover/tablerow:bg-primary",
    },
};

const entityCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "entity",
    accessorKey: "entity",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.entity);
    },
    cell: ({ row }) => row.original?.entity?.name,
    meta: {
        colWidth: "106px",
        headerClassName: "min-w-[106px] max-w-[106px] text-secondary",
        cellClassName:
            "min-w-[106px] max-w-[106px] group-hover/tablerow:bg-primary",
    },
};

const dueDateCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "dueDate",
    accessorKey: "dueDate",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.dueDate);
    },
    cell: ({ row }) =>
        dayjs(row.original?.objective?.target?.dueDate).format("LL"),
    meta: {
        colWidth: "100px",
        headerClassName: "min-w-[100px] max-w-[100px] text-secondary",
        cellClassName:
            "min-w-[100px] max-w-[100px] group-hover/tablerow:bg-primary",
    },
};

const valueCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "value",
    accessorKey: "value",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.value);
    },
    cell: ({ row }) => row.original?.customItem?.value,
    meta: {
        colWidth: "206px",
        headerClassName: "min-w-[206px] max-w-[206px] text-secondary",
        cellClassName:
            "min-w-[206px] max-w-[206px] group-hover/tablerow:bg-primary",
    },
};

const descriptionCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "description",
    accessorKey: "description",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.description);
    },
    cell: ({ row }) => row.original?.customItem?.description,
    meta: {
        colWidth: "206px",
        headerClassName: "min-w-[206px] max-w-[206px] text-secondary",
        cellClassName:
            "min-w-[206px] max-w-[206px] group-hover/tablerow:bg-primary",
    },
};

const targetCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "target",
    accessorKey: "target",
    enableSorting: false,
    header: () => {
        const { t, keys } = useKnowledgeBasePageTranslations();
        return t(keys.target);
    },
    cell: ({ row }) => {
        if (!row.original?.objective?.target?.numberValue) {
            return " - ";
        }
        if (!row.original?.objective?.unit) {
            return `${row.original?.objective?.target?.numberValue}`;
        }
        return `${row.original?.objective?.target?.numberValue} ${row.original?.objective?.unit}`;
    },

    meta: {
        colWidth: "100px",
        headerClassName: "min-w-[100px] max-w-[100px] text-secondary",
        cellClassName:
            "min-w-[100px] max-w-[100px] group-hover/tablerow:bg-primary",
    },
};

const actionCol: ColumnDef<KnowledgeBaseListItem> = {
    id: "action",
    cell: ({ row }) => {
        const [editModalOpen, setEditModalOpen] = useState(false);
        const [deleteModalOpen, setDeleteModalOpen] = useState(false);
        const { t, keys } = useKnowledgeBasePageTranslations();
        const { setToastError, setToastSuccess } = useSetToast();
        const { updateCustomItem } = useUpdateCustomKnowledgeBaseItem(
            row.original.externalId,
        );
        const { deleteCustomItem } = useDeleteCustomKnowledgeBaseItem(
            row.original.externalId,
        );
        const [description, setDescription] = useState(
            row.original.customItem?.description,
        );
        const [value, setValue] = useState<string | undefined>(
            row.original.customItem?.value ?? "",
        );

        const handleClickDelete = () => {
            setDeleteModalOpen(true);
        };
        const handleEdit = () => {
            setEditModalOpen(true);
        };

        const handleSave = (
            description: string | undefined,
            value: string | undefined,
        ) => {
            if (!description) {
                setToastError(t(keys.update_custom_item_error));
                return;
            }
            updateCustomItem(
                { description, value },
                {
                    onSettled: (data) => {
                        if (
                            data?.updateCustomItem?.status ===
                            ResponseStatus.Success
                        ) {
                            setEditModalOpen(false);
                            return setToastSuccess(
                                t(keys.update_custom_item_success),
                            );
                        }
                        return setToastError(t(keys.update_custom_item_error));
                    },
                },
            );
        };

        const handleDelete = () => {
            deleteCustomItem(undefined, {
                onSettled: (data) => {
                    if (
                        data?.deleteCustomItem?.status ===
                        ResponseStatus.Success
                    ) {
                        return setToastSuccess(
                            t(keys.delete_custom_item_success),
                        );
                    }
                    return setToastError(t(keys.delete_custom_item_error));
                },
            });
        };
        return (
            <FlexRow className="justify-center" gap="2">
                <Button variant="outlined" size="sm" onClick={handleEdit}>
                    <Icon name="edit" size="sm" />
                </Button>
                <Button
                    variant="outlined"
                    size="sm"
                    onClick={handleClickDelete}
                >
                    <Icon name="trash" size="sm" />
                </Button>
                <AddCustomItemModal
                    open={editModalOpen}
                    onOpenChange={setEditModalOpen}
                    onSave={handleSave}
                    description={description}
                    onDescriptionChange={setDescription}
                    value={value}
                    onValueChange={setValue}
                />
                <DeleteCustomItemModal
                    open={deleteModalOpen}
                    onOpenChange={setDeleteModalOpen}
                    onDelete={handleDelete}
                />
            </FlexRow>
        );
    },
    meta: {
        colWidth: "104px",
        headerClassName: "min-w-[104px] max-w-[104px]",
        cellClassName:
            "min-w-[104px] max-w-[104px] group-hover/tablerow:bg-primary",
    },
};

export const useColumns = (
    activeGroupId: string,
    canSelectAll: boolean,
): ColumnDef<KnowledgeBaseListItem>[] => {
    const columns = useMemo(
        () => [
            selectCol(canSelectAll),
            aiVisibilityStatusCol,
            ...(activeGroupId !== KnowledgeBaseItemType.Indicator &&
            activeGroupId !== KnowledgeBaseItemType.CustomItem
                ? [statusCol]
                : []),
            ...(activeGroupId === KnowledgeBaseItemType.CustomItem
                ? [descriptionCol, valueCol]
                : [nameCol(activeGroupId)]),

            ...(activeGroupId === KnowledgeBaseItemType.Framework
                ? [projectNameCol]
                : []),
            ...([
                KnowledgeBaseItemType.Indicator,
                KnowledgeBaseItemType.Objective,
            ].includes(activeGroupId as KnowledgeBaseItemType)
                ? [entityCol]
                : []),
            lastModifiedAtCol,
            ...(activeGroupId === KnowledgeBaseItemType.Document
                ? [typeCol]
                : []),
            ...(activeGroupId === KnowledgeBaseItemType.Objective
                ? [dueDateCol, targetCol]
                : []),
            ...(activeGroupId === KnowledgeBaseItemType.CustomItem
                ? [actionCol]
                : [linkCol]),
        ],
        [activeGroupId, canSelectAll],
    );
    return columns;
};

export const pinnedLeftColumnsIds: string[] = [
    "checkbox",
    "aiVisibilityStatus",
];
export const pinnedRightColumnsIds: string[] = ["link"];
